import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { buildDeck } from '../../domain/game/overtakeDeck';
import { startMatch } from '../../domain/game/overtakeMatch';
import { applyMove } from '../../domain/game/overtakeMoves';
import {
    buildMatchDocument,
    canWriteOver,
    isMatchId,
    isOpenForJoining,
    isValidMatchDocument,
    newMatchId,
    seatFor,
    seatOpponent,
} from '../../domain/game/overtakeInvite';
import { createMatch, watchMatch, writeMatch } from '../../infrastructure/cloud/matchClient';
import { useAuth } from '../account/AuthProvider';

/**
 * A match played against somebody else's phone (SPEC-011 PR-11.4, ADR-0006).
 *
 * The match id lives in the QUERY STRING — `/en/play?match=…` — for the same
 * reason the comparison's pair does (SPEC-008): it is the thing worth sending
 * somebody, and a link is the entire invitation mechanism. There is no lobby and
 * no matchmaking, so an unguessable id in a URL is not a shortcut, it is the
 * security model (ADR-0006).
 *
 * THE DECK IS THE WHOLE CATALOGUE, not the reader's unlocked cars, and that is
 * the one place an invited match differs from a local one. A local match deals
 * from what the reader owns (SPEC-010), which is right when there is only one
 * collection involved; two players have two, and a guest who has not yet won the
 * Supra still has to be able to see the Supra the host just played. The host's
 * deal decides what is in the match, and the ids in the document resolve against
 * everything.
 */

/** The query parameter a match is invited by. */
export const MATCH_PARAM = 'match';

export default function useOvertakeInvite({ cars }) {
    const { authStatus, user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const requested = searchParams.get(MATCH_PARAM);
    const matchId = isMatchId(requested) ? requested : null;
    const deck = useMemo(() => buildDeck(cars), [cars]);
    const ids = useMemo(() => [...deck.keys()], [deck]);
    const [document, setDocument] = useState(null);
    /**
     * Where this reader is in the invitation, as one word.
     *
     *   idle      no match in the URL. The screen offers to create one.
     *   loading   a match id is in the URL and the first snapshot has not landed.
     *   ready     the document is open and playable, or watchable.
     *   missing   the id resolves to nothing. A stale link, or a typo.
     *   refused   the document exists and this build cannot act on it — a shape
     *             from a version that has not shipped here.
     *   failed    the network or the rules said no. The reader can still play
     *             locally, and the copy says so.
     */
    const [status, setStatus] = useState(matchId ? 'loading' : 'idle');

    /**
     * Watch the match.
     *
     * Re-subscribed on the id alone. The document changing must NOT restart the
     * listener — that is a subscription torn down and rebuilt on every move by
     * either player, which is both a burst of reads on a shared quota and a
     * window in which a move can land unseen.
     */
    useEffect(() => {
        if (!matchId) {
            setDocument(null);
            setStatus('idle');

            return undefined;
        }

        setStatus('loading');

        return watchMatch(matchId, {
            onChange: (data) => {
                if (!data) {
                    setStatus('missing');
                    setDocument(null);

                    return;
                }

                if (!isValidMatchDocument(data)) {
                    setStatus('refused');
                    setDocument(null);

                    return;
                }

                setDocument(data);
                setStatus('ready');
            },
            // A match that cannot be reached is a stated message and the local
            // game beside it, never a thrown screen — the posture SPEC-009 took
            // for the board, and for the same reason: this is a card game.
            onError: (error) => {
                console.error(error);
                setStatus('failed');
            },
        });
    }, [matchId]);

    /** Deal a match, write it, and put its id in the URL for sharing. */
    const create = useCallback(async (options) => {
        if (!user) {
            return null;
        }

        const id = newMatchId();
        const state = startMatch(ids, options);

        if (state.status === 'unavailable') {
            return null;
        }

        try {
            await createMatch(id, buildMatchDocument({ state, host: user.uid, name: user.name }));
            // `replace`, so an invitation is not a back-button step: a reader who
            // presses back should leave the game, not un-create a match that now
            // exists on a server.
            setSearchParams((current) => {
                const next = new URLSearchParams(current);

                next.set(MATCH_PARAM, id);

                return next;
            }, { replace: true });

            return id;
        } catch (error) {
            console.error(error);
            setStatus('failed');

            return null;
        }
    }, [ids, setSearchParams, user]);

    /** Take the empty seat. */
    const join = useCallback(async () => {
        if (!matchId || !document || !user || !isOpenForJoining(document, user.uid)) {
            return;
        }

        try {
            await writeMatch(matchId, seatOpponent(document, { uid: user.uid, name: user.name }));
        } catch (error) {
            console.error(error);
            setStatus('failed');
        }
    }, [document, matchId, user]);

    /**
     * Play a move.
     *
     * Validated twice before it is sent and once after it arrives: the move has
     * to be this reader's to make (`canWriteOver`), it has to change the state
     * (`applyMove` answers its own argument for an illegal move, so identity is
     * the check), and the result has to be a document the other client will
     * accept. A write that fails any of the three is a bug here rather than a
     * routine outcome, which is the standard SPEC-009 set for the board.
     */
    const move = useCallback(async (played) => {
        if (!matchId || !document || !user || !canWriteOver(document, user.uid)) {
            return;
        }

        const state = applyMove(document.state, deck, played);

        if (state === document.state) {
            return;
        }

        try {
            await writeMatch(matchId, { ...document, state });
        } catch (error) {
            console.error(error);
            setStatus('failed');
        }
    }, [deck, document, matchId, user]);

    /** Walk away from the invitation and back to the local game. */
    const leave = useCallback(() => setSearchParams((current) => {
        const next = new URLSearchParams(current);

        next.delete(MATCH_PARAM);

        return next;
    }, { replace: true }), [setSearchParams]);

    const seat = document && user ? seatFor(document, user.uid) : null;

    return {
        matchId,
        status,
        document,
        deck,
        // The seat this account holds, or null for somebody who arrived at a
        // match that is already full. A spectator is a designed state.
        seat,
        canJoin: Boolean(user) && isOpenForJoining(document, user?.uid),
        needsAccount: authStatus !== 'authenticated',
        create,
        join,
        move,
        leave,
    };
}
